import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { commodityTypeMasterApi } from "../API/getCommodityTypeMaster";
import {
  saveFilterCommodityTypeData,
} from "../AppState/overagesFilterFormActions";

/**
 * Custom hook for managing commodity master data
 *
 * This hook encapsulates the common commodity code loading logic used across multiple components.
 * It fetches commodity options from the commodityTypeMasterApi, sorts them, and saves them to Redux state.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { loadCommodityOptions } = useCommodityMaster();
 *   const commodityOptions = useSelector((state: AppState) => state.commodityMasterData);
 *
 *   useEffect(() => {
 *     if (!commodityOptions?.length) {
 *       loadCommodityOptions();
 *     }
 *   }, [loadCommodityOptions, commodityOptions?.length]);
 *
 *   // Component JSX...
 * }
 * ```
 *
 * @returns {Object} Hook methods
 * @returns {Function} loadCommodityOptions - Function to load and initialize commodity master data
 */
export const useCommodityMaster = () => {
  const dispatch = useDispatch();

  /**
   * Fetches commodity options, sorts them, and saves them to Redux state
   *
   * This function:
   * 1. Calls the commodityTypeMasterApi API
   * 2. Trims the display values and sorts by display value
   * 3. Dispatches the results to Redux using saveFilterCommodityTypeData
   * 4. Handles errors gracefully with console logging in development
   */
  const loadCommodityOptions = useCallback(async () => {
    try {
      const result = await commodityTypeMasterApi();
      dispatch(saveFilterCommodityTypeData(result));
    } catch (error) {
      console.error("Error loading commodity options:", error);
    }
  }, [dispatch]);

  return {
    loadCommodityOptions,
  };
};
